import { DelegatedEvent, EventCallback } from "../types/EventDelegationTypes.js";

/**
 * Delegate - Standalone Event Delegation Class
 * 
 * Provides jQuery-style event delegation functionality for any DOM element.
 * Can be used independently or within web components.
 * 
 * @example
 * // Instance usage
 * const delegate = new Delegate(document.body);
 * delegate.on('click', '.btn', handleButtonClick);
 * delegate.off('click', '.btn', handleButtonClick);
 * delegate.destroy(); // Clean up all listeners
 * 
 * // Static usage (global delegation on document.body)
 * Delegate.on('click', '.btn', handleButtonClick);
 * Delegate.off('click', '.btn', handleButtonClick);
 */
export class Delegate {
    private static globalInstance: Delegate | null = null;
    private delegatedEvents: DelegatedEvent<any>[] = [];
    private rootElement: Element;

    constructor(rootElement: Element = document.body) {
        this.rootElement = rootElement;
    }

    /**
     * Add event delegation (similar to jQuery.on)
     * @param type - Event type (e.g., 'click', 'input', 'change')
     * @param selector - CSS selector to match target elements
     * @param handler - Event handler function
     */
    on<T extends Event = Event>(type: string, selector: string, handler: EventCallback<T>): void {
        const wrappedHandler = (event: Event) => {
            const target = event.target as Element;
            if (target && this.rootElement.contains(target)) {
                // Find the element that matches the selector
                const matchingElement = target.closest(selector);
                if (matchingElement) {
                    // Copy properties from the original event and set the new target
                    Object.defineProperty(event, 'target', {
                        value: matchingElement,
                        writable: false,
                        configurable: true
                    });

                    // Call the handler with the original event (now with correct target)
                    // The type assertion is safe because we constrain T to extend Event
                    handler.call(matchingElement, event as T);
                }
            }
        };

        this.rootElement.addEventListener(type, wrappedHandler);

        this.delegatedEvents.push({
            type,
            selector,
            handler,
            wrapped: wrappedHandler,
        });
    }

    /**
     * Remove event delegation (similar to jQuery.off)
     * @param type - Event type
     * @param selector - CSS selector
     * @param handler - Event handler function to remove
     */
    off<T extends Event = Event>(type: string, selector: string, handler: EventCallback<T>): void {
        const matches = this.delegatedEvents.filter(
            ev =>
                ev.type === type &&
                ev.selector === selector &&
                ev.handler === handler
        );

        for (const ev of matches) {
            this.rootElement.removeEventListener(ev.type, ev.wrapped);
        }

        this.delegatedEvents = this.delegatedEvents.filter(
            ev =>
                !(
                    ev.type === type &&
                    ev.selector === selector &&
                    ev.handler === handler
                )
        );
    }

    /**
     * Remove all event listeners for a specific type and selector
     * @param type - Event type
     * @param selector - CSS selector
     */
    offAll(type: string, selector: string): void {
        const matches = this.delegatedEvents.filter(
            ev => ev.type === type && ev.selector === selector
        );

        for (const ev of matches) {
            this.rootElement.removeEventListener(ev.type, ev.wrapped);
        }

        this.delegatedEvents = this.delegatedEvents.filter(
            ev => !(ev.type === type && ev.selector === selector)
        );
    }

    /**
     * Remove all delegated event listeners
     */
    destroy(): void {
        for (const ev of this.delegatedEvents) {
            this.rootElement.removeEventListener(ev.type, ev.wrapped);
        }
        this.delegatedEvents = [];
    }

    /**
     * Get count of active delegated events
     */
    getEventCount(): number {
        return this.delegatedEvents.length;
    }

    /**
     * Get list of active delegated events (for debugging)
     */
    getEvents(): readonly Omit<DelegatedEvent<any>, 'wrapped'>[] {
        return this.delegatedEvents.map(({ type, selector, handler }) => ({
            type,
            selector,
            handler
        }));
    }

    /**
     * Change the root element for delegation
     * This will remove all existing listeners and reset the delegate
     * @param newRootElement - New root element
     */
    setRootElement(newRootElement: Element): void {
        this.destroy();
        this.rootElement = newRootElement;
    }

    /**
     * Check if the delegate has any listeners for a specific type/selector combination
     * @param type - Event type
     * @param selector - CSS selector
     */
    hasListeners(type: string, selector: string): boolean {
        return this.delegatedEvents.some(
            ev => ev.type === type && ev.selector === selector
        );
    }

    // =============================
    // Static Methods (Global Instance)
    // =============================

    /**
     * Get or create the global delegate instance
     */
    private static getGlobalInstance(): Delegate {
        if (!this.globalInstance) {
            this.globalInstance = new Delegate(document.body);
        }
        return this.globalInstance;
    }

    /**
     * Static method to add event delegation on document.body
     * @param type - Event type
     * @param selector - CSS selector
     * @param handler - Event handler function
     */
    static on<T extends Event = Event>(type: string, selector: string, handler: EventCallback<T>): void {
        this.getGlobalInstance().on<T>(type, selector, handler);
    }

    /**
     * Static method to remove event delegation
     * @param type - Event type
     * @param selector - CSS selector
     * @param handler - Event handler function
     */
    static off<T extends Event = Event>(type: string, selector: string, handler: EventCallback<T>): void {
        this.getGlobalInstance().off<T>(type, selector, handler);
    }

    /**
     * Static method to remove all event listeners for a specific type and selector
     * @param type - Event type
     * @param selector - CSS selector
     */
    static offAll(type: string, selector: string): void {
        this.getGlobalInstance().offAll(type, selector);
    }

    /**
     * Static method to destroy all global delegated events
     */
    static destroy(): void {
        if (this.globalInstance) {
            this.globalInstance.destroy();
            this.globalInstance = null;
        }
    }

    /**
     * Static method to get count of active delegated events
     */
    static getEventCount(): number {
        return this.globalInstance ? this.globalInstance.getEventCount() : 0;
    }

    /**
     * Static method to check if there are listeners for a specific type/selector
     * @param type - Event type
     * @param selector - CSS selector
     */
    static hasListeners(type: string, selector: string): boolean {
        return this.globalInstance ? this.globalInstance.hasListeners(type, selector) : false;
    }
}